var _ = require('underscore');
var moment = require('moment');
var AuthFormView = require('./../../authentication/AuthFormView');
var eregAuthFormTemplate = require('./eregAuthFormTemplate.hbs');
var UseModalBehavior = require('./../../widget/modal/UseModalBehavior');
var ModalDialogWidget = require('./../../widget/modal/ModalDialogWidget');
var EregAuthConfigModel = require('./EregAuthConfigModel');
var dobConfig = require('../pickadateConfigs').dob;
var eregAuthConfigOptions = require('./eregAuthConfigOptions');
var userService = require('./../../user/userService');
var sessionStorageService = require('./../../storage/sessionStorageService');
var eregWorkflowConfigService = require('./../../wizard/eregWorkflowConfigService');
var applicationUtils = require('./../../applicationUtils');
var stringUtils = require('./../../utils/stringUtils');
var routerUtils = require('./../../utils/routerUtils');
var wizardUtils = require('./../../wizard/wizardUtils');

require('pickadate/lib/picker.date.js');

var SERIAL_NUMBER_PROPERTY_NAME = sessionStorageService.SESSION_STORAGE_KEYS.serialNumber;

/**
 * Uses moment to convert the date string from the fromDateFormat pattern to the toDateFormat pattern.
 * @param date {String} the date to convert.
 * @param fromDateFormat {String} pattern that moment should use for the incoming date.
 * @param toDateFormat {String} pattern that moment should use for the returned format.
 * @returns {String} the re-formatted date or '' if the date could not be formatted correctly.
 */
function convertDateToNewFormat(date, fromDateFormat, toDateFormat) {
  var momentDate = moment(date, fromDateFormat);
  if (date && momentDate.isValid()) {
    return momentDate.format(toDateFormat);
  } else {
    return '';
  }
}

/**
 * Form view for E-reg patient authentication
 *
 * @class EregAuthFormView
 * @constructor
 * @param options {Object}
 *   @param [options.permissionRole='portal.patient'] {String} the permission that the user needs to successfully authenticate.
 *   @param [options.navigateToPageFn=_.noop] {Function} a function that is run when authentication is successful.
 */
module.exports = AuthFormView.extend({
  className: 'brec-authentication__main',
  moduleNamespace: 'eregAuthFormView',
  template: eregAuthFormTemplate,

  events: {
    'submit': '_submit',
    'click [data-login-form-clear-fields]': '_clearFormData'
  },

  behaviors: {
    UseModalBehavior: UseModalBehavior
  },

  _errorCodeMap: {
    0: { patientAuthConnectionError: true },
    401: function () {
      this._openModal();
    },
    403: { patientAuthSerialNumberError: true },
    unknown: { patientAuthError: true }
  },

  authValidation: {
    firstName: {
      required: true
    },
    lastName: {
      required: true
    },
    dob: {
      required: true
    },
    phone: {
      required: true,
      //Same pattern as com.vecna.persistence.geo.data.PhoneNumber
      pattern: '^\\+?(\\d{0,3})??[ -]?\\(?(\\d{3})?\\)?\\/? ?-? ?(\\d{3}) ?-? ?(\\d{4})(?:(?:[xX]|[xX]\\.|ext\\.|\\-| )+(\\d{0,6}))?$'
    },
    patientId: {
      required: true
    },
    lastFourSsn: {
      required: true,
      pattern: 'digits'
    }
  },

  fields: {
    firstName: {
      modelFormat: stringUtils.nullSafeTrim,
      viewFormat: stringUtils.nullSafeTrim
    },

    lastName: {
      modelFormat: stringUtils.nullSafeTrim,
      viewFormat: stringUtils.nullSafeTrim
    },

    dob: {
      modelFormat: function (dob) {
        var fromDateFormat = dobConfig.momentFormat,
          toDateFormat = dobConfig.momentFormatSubmit;
        return convertDateToNewFormat(dob, fromDateFormat, toDateFormat);
      },

      viewFormat: function (dob) {
        var fromDateFormat = dobConfig.momentFormatSubmit,
          toDateFormat = dobConfig.momentFormat;
        return convertDateToNewFormat(dob, fromDateFormat, toDateFormat);
      }
    }
  },

  // ----- Overrides ---------------------------------------------------------------------------------------------------------------

  /**
   * @method initialize
   * @param options {Object}
   *   @param [options.permissionRole='portal.patient'] {String} the permission that the user needs to successfully authenticate.
   *   @param [options.navigateToPageFn=_.noop] {Function} a function that is run when authentication is successful.
   * @override
   */
  initialize: function() {
    AuthFormView.prototype.initialize.apply(this, arguments);
    // Bind this context to methods used by promises
    _.bindAll(this, '_filterValidationOptions',
                    '_setConfigLoadErrors',
                    '_displayLoadResult',
                    '_buildAuthRedirectUrl',
                    '_navigateToPageFn',
                    '_clearFormData');

    // Set up the authentication model
    // save things to the patient endpoint of the authentication service.
    this._authModel.urlRoot = this._authModel.urlRoot + '/patient';
    // Set the serial number of the tablet being used.
    this._authModel.set('serialNumber', sessionStorageService.getItem(SERIAL_NUMBER_PROPERTY_NAME));
    // Remove permissionRole since the endpoint does not expect this.
    this._authModel.unset('permissionRole');

    // Load the configuration
    this.set('loadingConfigs', true);
    this._configModel = new EregAuthConfigModel();
    this._configModel
      .fetch()
      .done(this._filterValidationOptions)
      .fail(this._setConfigLoadErrors)
      .always(this._displayLoadResult);
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var templateContext = AuthFormView.prototype.prepare.apply(this);
    templateContext.config = this.model.validation;
    return templateContext;
  },

  /**
   * @method postrender
   * @override
   */
  postrender: function() {
    this.$('.auth-main__login-form-input--dob').pickadate(dobConfig);
  },

  // ----- Helpers -----------------------------------------------------------------------------------------------------------------

  /**
   * Called on authentication success. Clears out models and forwards the user to the patient search page.
   *
   * @private
   * @method _navigateToPageFn
   */
  _navigateToPageFn: function() {
    window.location.href = this._buildAuthRedirectUrl();
  },

  /**
   * Build the redirect url after the user is authenticated.
   * @private
   * @method _buildAuthRedirectUrl
   * @returns {String} url for the ereg if ereg workflow config exists for the authenticated user, otherwise the url for appointment
       list page.
   */
  _buildAuthRedirectUrl: function() {
    var currentUserModel = userService.currentUserModel;
    var currentPatientVecnaId = currentUserModel.get('vecnaId');
    var redirectUrl = applicationUtils.currentApplication.fullUrlPath + '#patient/' + currentPatientVecnaId + '/appointment';
    var eregWorkflowConfig = eregWorkflowConfigService.getEregWorkflowConfig();
    if (eregWorkflowConfig && currentUserModel && eregWorkflowConfig.patientVecnaId === currentPatientVecnaId) {
      redirectUrl = wizardUtils.generateRootRegUrl({
        patientVecnaId: currentPatientVecnaId,
        appointmentExternalId: eregWorkflowConfig.appointmentExtId,
        regDate: eregWorkflowConfig.regDate
      });
    } else {
      eregWorkflowConfigService.removeEregWorkflowConfig();
    }
    return routerUtils.cleanRedirectURL(redirectUrl);
  },

  /**
   * Sets the model validation to only use the fields returned by the config service.
   * Uses the eregAuthConfigOptions to translate between the front-end and back-end objects.
   *
   * @param response {Object} The response from the config service that contains the fields to use.
   * @private
   * @method _filterValidationOptions
   */
  _filterValidationOptions: function(response) {
    this.model.validation = _.pick(this.authValidation, function(value, key) {
      return response.tabletAuthenticationFields[eregAuthConfigOptions[key]] === true;
    });
  },

  /**
   * Pulls the config load error by its i18n key and sets it on the view state.
   * @private
   * @method _setConfigLoadErrors
   */
  _setConfigLoadErrors: function() {
    this.set('errors', { configLoadError: true });
  },

  /**
   * Display the results of loading the configuration.
   * @private
   * @method _displayLoadResult
   */
  _displayLoadResult: function() {
    this.set('loadingConfigs', false);
  },

  /**
   * Sets up the listeners and sets the showModal state to true
   * @private
   * @method _openModal
   */
  _openModal: function() {
    this._noMatchingProfileModal = new ModalDialogWidget({
      dataViewName: 'ereg no matching profile',
      headerKey: 'eregAuth.modal.header',
      descriptionKey: 'eregAuth.modal.body',
      alertOnly: true,
      closeKey: 'eregAuth.modal.tryAgain',
      closeTitleKey: 'eregAuth.modal.close'
    });
    this.showModal(this._noMatchingProfileModal, null, this._clearFormData);
  }
});
